/**
 * Try the native Web Share API first on mobile.
 *
 * If the device supports `navigator.share` AND we're on a touch device,
 * we trigger the OS share sheet directly — that's the best UX (one tap,
 * native UI, includes Instagram Stories handoff on iOS).
 *
 * Returns:
 *   true  — the native share sheet was shown (success or user-cancel).
 *           The caller should NOT also open the custom menu.
 *   false — native share unavailable. Caller should open the custom menu.
 */
export async function tryNativeShare({ title, text, url }) {
  if (typeof navigator === 'undefined' || !navigator.share) return false;

  // Only auto-fire on touch-first devices. On desktop Chrome `navigator.share`
  // also exists but the OS sheet there is clunky, so we prefer our menu.
  const isTouch =
    'ontouchstart' in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
  if (!isTouch) return false;

  try {
    await navigator.share({ title, text, url });
    return true;
  } catch (err) {
    // AbortError == user cancelled. Still treat as handled so we don't
    // double-up the custom menu over the just-dismissed sheet.
    if (err && err.name === 'AbortError') return true;
    return false;
  }
}
